import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import MarkdownMessage from "./MarkdownMessage";

const MODE_ACTIONS = ["Code Mode", "Standard", "Preceptor Mode"];

export default function ChatPanel({ messages, onSend, loading, suggestQuick = true }) {
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const submit = (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    onSend(text);
    setInput("");
  };

  const quickFire = (txt) => {
    if (loading) return;
    onSend(txt);
  };

  return (
    <div className="flex flex-col h-full bg-[#fbf8f2] border border-[#e4ded2] rounded-sm">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto scroll-clean p-6 space-y-5"
        data-testid="chat-messages"
      >
        {messages.length === 0 && (
          <div className="text-[#9c8f7e] text-sm">Starting session…</div>
        )}
        {messages.map((m, i) => (
          <div key={i} className="msg-in" data-testid={`msg-${m.role}`}>
            <div
              className={`text-[10px] uppercase tracking-widest mb-1.5 font-medium ${
                m.role === "assistant" ? "text-[#6f2a30]" : "text-[#1a1612]"
              }`}
            >
              {m.role === "assistant" ? "ICU Brain" : "You"}
            </div>
            {m.role === "assistant" ? (
              <div className="border-l-2 border-[#6f2a30] pl-4">
                <MarkdownMessage>{m.content}</MarkdownMessage>
              </div>
            ) : (
              <div className="text-[15px] leading-relaxed whitespace-pre-wrap text-[#3a322b] pl-4 border-l-2 border-[#e4ded2]">
                {m.content}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-[#6b6258] text-sm">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span className="font-mono">thinking…</span>
          </div>
        )}
      </div>

      {suggestQuick && (
        <div className="px-6 pt-3 pb-2 border-t border-[#e4ded2] flex flex-wrap gap-1.5">
          <span className="text-[10px] uppercase tracking-widest text-[#9c8f7e] self-center mr-1">
            Switch:
          </span>
          {MODE_ACTIONS.map((q) => (
            <button
              key={q}
              onClick={() => quickFire(q)}
              data-testid={`quick-${q.replace(/\s+/g, "-").toLowerCase()}`}
              className="text-[11px] uppercase tracking-wider font-medium px-2.5 py-1 border border-[#e4ded2] text-[#6b6258] hover:bg-[#6f2a30] hover:text-[#f1eee7] hover:border-[#6f2a30] transition-colors rounded-sm"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="border-t border-[#e4ded2] p-3 flex gap-2 items-center">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your decision or question…"
          data-testid="chat-input"
          className="flex-1 px-4 py-2.5 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm text-[#1a1612] focus:outline-none focus:border-[#6f2a30] focus:bg-[#fbf8f2] transition-colors"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          data-testid="chat-send-btn"
          className="bg-[#6f2a30] text-[#f1eee7] px-4 py-2.5 rounded-sm hover:bg-[#582227] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm"
        >
          <Send className="h-4 w-4" strokeWidth={1.8} />
          <span className="hidden sm:inline">Send</span>
        </button>
      </form>
    </div>
  );
}
